/**
 * Scaling Up Vision (OPSP) editor.
 * Holding-level OR company-level (companyId optional). Single record per scope.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { AdminGuard } from "@/components/auth/AdminGuard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Save } from "lucide-react";

interface Holding {
  id: string;
  name: string;
}
interface Company {
  id: string;
  name: string;
}
interface Vision {
  id?: string;
  holdingId: string;
  companyId: string | null;
  coreValues: string[];
  corePurpose: string | null;
  bhag: string | null;
  threeYearTargets: Record<string, unknown>;
  oneYearGoals: Record<string, unknown>;
  brandPromises: string[];
  sevenStrata: Record<string, unknown>;
}

function safeJsonStringify(value: unknown): string {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return "{}";
  }
}

function safeJsonParse(value: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(value);
    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function ScalingUpVisionInner() {
  const qc = useQueryClient();
  const [holdingId, setHoldingId] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string>("");

  const holdingsQuery = useQuery<{ items: Holding[] }>({
    queryKey: ["/api/scaling-up/holdings"],
  });
  const companiesQuery = useQuery<{ items: Company[] }>({
    queryKey: ["/api/scaling-up/holdings", holdingId ?? "", "companies"],
    enabled: holdingId !== null,
  });
  const visionQuery = useQuery<Vision | null>({
    queryKey: ["/api/scaling-up/holdings", holdingId ?? "", `vision?companyId=${companyId}`],
    enabled: holdingId !== null,
  });

  const [bhag, setBhag] = useState("");
  const [corePurpose, setCorePurpose] = useState("");
  const [coreValues, setCoreValues] = useState<string>("");
  const [brandPromises, setBrandPromises] = useState<string>("");
  const [threeYearTargetsJson, setThreeYearTargetsJson] = useState("{}");
  const [oneYearGoalsJson, setOneYearGoalsJson] = useState("{}");
  const [sevenStrataJson, setSevenStrataJson] = useState("{}");

  useEffect(() => {
    const v = visionQuery.data ?? null;
    setBhag(v?.bhag ?? "");
    setCorePurpose(v?.corePurpose ?? "");
    setCoreValues((v?.coreValues ?? []).join("\n"));
    setBrandPromises((v?.brandPromises ?? []).join("\n"));
    setThreeYearTargetsJson(safeJsonStringify(v?.threeYearTargets));
    setOneYearGoalsJson(safeJsonStringify(v?.oneYearGoals));
    setSevenStrataJson(safeJsonStringify(v?.sevenStrata));
  }, [visionQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!holdingId) throw new Error("holdingId required");
      return apiRequest("PUT", `/api/scaling-up/holdings/${holdingId}/vision`, {
        companyId: companyId || null,
        bhag: bhag || null,
        corePurpose: corePurpose || null,
        coreValues: coreValues
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        brandPromises: brandPromises
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        threeYearTargets: safeJsonParse(threeYearTargetsJson),
        oneYearGoals: safeJsonParse(oneYearGoalsJson),
        sevenStrata: safeJsonParse(sevenStrataJson),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["/api/scaling-up/holdings", holdingId ?? "", `vision?companyId=${companyId}`],
      });
    },
  });

  const holdings = holdingsQuery.data?.items ?? [];
  const companies = companiesQuery.data?.items ?? [];
  const isSaving = saveMutation.isPending;

  const saveDisabled = useMemo(() => !holdingId || isSaving, [holdingId, isSaving]);

  return (
    <div className="min-h-screen bg-[#030407] text-white p-6 lg:p-10">
      <header className="mb-8">
        <div className="text-white/40 text-xs uppercase tracking-widest mb-1">Scaling Up</div>
        <h1 className="text-2xl lg:text-3xl font-extrabold">Vision (One-Page Strategic Plan)</h1>
      </header>

      <Card className="mb-6 border-white/10 bg-white/[0.02]">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <select
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm"
            value={holdingId ?? ""}
            onChange={(e) => {
              setHoldingId(e.target.value || null);
              setCompanyId("");
            }}
          >
            <option value="">Wybierz holding…</option>
            {holdings.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
          <select
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm disabled:opacity-50"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            disabled={!holdingId}
          >
            <option value="">Holding-level (bez spółki)</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      {holdingId && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section title="BHAG (Big Hairy Audacious Goal — 10-25 lat)">
            <textarea
              className="w-full bg-black/30 border border-white/10 rounded p-3 text-sm h-24"
              value={bhag}
              onChange={(e) => setBhag(e.target.value)}
              placeholder="np. Stworzyć 100 marek instagramowych przekraczających 1M followersów."
            />
          </Section>

          <Section title="Core Purpose">
            <textarea
              className="w-full bg-black/30 border border-white/10 rounded p-3 text-sm h-24"
              value={corePurpose}
              onChange={(e) => setCorePurpose(e.target.value)}
              placeholder="Po co istnieje firma — oprócz zarabiania pieniędzy?"
            />
          </Section>

          <Section title="Core Values (jeden na linię)">
            <textarea
              className="w-full bg-black/30 border border-white/10 rounded p-3 text-sm h-32 font-mono"
              value={coreValues}
              onChange={(e) => setCoreValues(e.target.value)}
              placeholder={"Egzekucja > pomysły\nProstota > kompletność\nDane > opinie"}
            />
          </Section>

          <Section title="Brand Promises (jeden na linię)">
            <textarea
              className="w-full bg-black/30 border border-white/10 rounded p-3 text-sm h-32 font-mono"
              value={brandPromises}
              onChange={(e) => setBrandPromises(e.target.value)}
              placeholder={"Pierwszy ad gotowy w 60 sekund\nROAS ≥ 2.5 lub zwracamy abonament"}
            />
          </Section>

          <Section title="3-Year Targets (JSON)">
            <textarea
              className="w-full bg-black/30 border border-white/10 rounded p-3 text-sm h-40 font-mono"
              value={threeYearTargetsJson}
              onChange={(e) => setThreeYearTargetsJson(e.target.value)}
            />
          </Section>

          <Section title="1-Year Goals (JSON)">
            <textarea
              className="w-full bg-black/30 border border-white/10 rounded p-3 text-sm h-40 font-mono"
              value={oneYearGoalsJson}
              onChange={(e) => setOneYearGoalsJson(e.target.value)}
            />
          </Section>

          <Section title="7 Strata of Strategy (JSON)">
            <textarea
              className="w-full bg-black/30 border border-white/10 rounded p-3 text-sm h-40 font-mono"
              value={sevenStrataJson}
              onChange={(e) => setSevenStrataJson(e.target.value)}
              placeholder='{"wordYouOwn":"…","sandbox":"…","brandPromiseGuarantee":"…","onePhrase":"…","differentiatingActivities":"…","xFactor":"…"}'
            />
          </Section>
        </div>
      )}

      {holdingId && (
        <div className="mt-6 flex items-center gap-3">
          <Button onClick={() => saveMutation.mutate()} disabled={saveDisabled}>
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Zapisz
          </Button>
          {saveMutation.isError && (
            <span className="text-red-300 text-sm">
              {saveMutation.error instanceof Error ? saveMutation.error.message : "Błąd zapisu"}
            </span>
          )}
          {saveMutation.isSuccess && <span className="text-emerald-300 text-sm">Zapisano</span>}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="border-white/10 bg-white/[0.02]">
      <CardContent className="p-5">
        <div className="text-white/60 text-xs uppercase tracking-wider mb-3">{title}</div>
        {children}
      </CardContent>
    </Card>
  );
}

export default function ScalingUpVisionPage() {
  return (
    <AdminGuard role="super_admin">
      <ScalingUpVisionInner />
    </AdminGuard>
  );
}
